import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Users, Mail, Settings, Plus, Search, Filter, 
  MoreVertical, Copy, QrCode, Eye, Edit, Trash2, Archive,
  ChevronRight, CheckCircle2, Clock, AlertCircle, Send,
  Download, ExternalLink, GripVertical, X, Check, Upload,
  Calendar, Building2, User, Phone, Globe, Link2, Loader2,
  LayoutList, LayoutGrid, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const API_BASE = `${window.location.origin}/api`;

// Status badge colors
const statusColors = {
  new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  reviewing: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  contacted: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  assigned: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  in_progress: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const statusLabels = {
  new: 'New',
  reviewing: 'Reviewing',
  contacted: 'Contacted',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed',
  archived: 'Archived',
};

const formStatusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  published: 'bg-green-500/20 text-green-400',
  archived: 'bg-red-500/20 text-red-400',
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function OnboardingAdmin() {
  const [activeTab, setActiveTab] = useState('forms');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [forms, setForms] = useState([]);
  const [responses, setResponses] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [stats, setStats] = useState({});
  
  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [showSendToDAModal, setShowSendToDAModal] = useState(false);
  const [selectedResponseForDA, setSelectedResponseForDA] = useState(null);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeData, setQRCodeData] = useState('');
  const [qrFormName, setQRFormName] = useState('');

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [formsRes, responsesRes, teamRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/onboarding/forms`),
        fetch(`${API_BASE}/onboarding/responses`),
        fetch(`${API_BASE}/onboarding/team-members`),
        fetch(`${API_BASE}/onboarding/stats`),
      ]);
      
      const [formsData, responsesData, teamData, statsData] = await Promise.all([
        formsRes.json(),
        responsesRes.json(),
        teamRes.json(),
        statsRes.json(),
      ]);
      
      setForms(formsData.forms || []);
      setResponses(responsesData.responses || []);
      setTeamMembers(teamData.team_members || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create default form
  const createDefaultForm = async () => {
    try {
      const res = await fetch(`${API_BASE}/onboarding/forms/create-default`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.ok) {
        toast.success('Default onboarding form created');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to create default form');
    }
  };

  // Create new blank form
  const createNewForm = async () => {
    try {
      const res = await fetch(`${API_BASE}/onboarding/forms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'New Onboarding Form',
          description: '',
          sections: [],
          status: 'draft',
        }),
      });
      const data = await res.json();
      if (data.ok) {
        toast.success('New form created');
        setEditingForm(data.form);
        setShowFormBuilder(true);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to create form');
    }
  };

  // Duplicate form
  const duplicateForm = async (formId) => {
    try {
      const res = await fetch(`${API_BASE}/onboarding/forms/${formId}/duplicate`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.ok) {
        toast.success('Form duplicated');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to duplicate form');
    }
  };

  // Delete form
  const deleteForm = async (formId) => {
    if (!confirm('Are you sure you want to delete this form?')) return;
    try {
      const res = await fetch(`${API_BASE}/onboarding/forms/${formId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Form deleted');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to delete form');
    }
  };

  // Publish/unpublish form
  const toggleFormStatus = async (formId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      const res = await fetch(`${API_BASE}/onboarding/forms/${formId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(newStatus === 'published' ? 'Form published' : 'Form unpublished');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to update form');
    }
  };

  // Copy share link
  const copyShareLink = (form) => {
    const link = `${window.location.origin}/onboarding/${form.slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  // Generate QR Code
  const generateQRCode = async (form) => {
    const link = `${window.location.origin}/onboarding/${form.slug}`;
    try {
      const qrDataUrl = await QRCode.toDataURL(link, { width: 300, margin: 2 });
      setQRCodeData(qrDataUrl);
      setQRFormName(form.name);
      setShowQRModal(true);
    } catch (err) {
      toast.error('Failed to generate QR code');
    }
  };

  // Update response status
  const updateResponseStatus = async (responseId, newStatus) => {
    try {
      const res = await fetch(`${API_BASE}/onboarding/responses/${responseId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success('Status updated');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Send to DA
  const sendToDA = async (responseId, daId) => {
    try {
      const res = await fetch(`${API_BASE}/onboarding/responses/${responseId}/send-to-da?da_id=${daId}`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.ok) {
        toast.success('Sent to Digital Assistant');
        setShowSendToDAModal(false);
        setSelectedResponseForDA(null);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to send to DA');
    }
  };

  // Add team member
  const addTeamMember = async (name, email, role) => {
    try {
      const res = await fetch(`${API_BASE}/onboarding/team-members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, role }),
      });
      const data = await res.json();
      if (data.ok) {
        toast.success('Team member added');
        setShowAddTeamMember(false);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to add team member');
    }
  };

  // Delete team member
  const deleteTeamMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;
    try {
      const res = await fetch(`${API_BASE}/onboarding/team-members/${memberId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Team member removed');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to remove team member');
    }
  };

  // Filter responses
  const filteredResponses = responses.filter(r => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        r.company_name?.toLowerCase().includes(query) ||
        r.contact_name?.toLowerCase().includes(query) ||
        r.form_name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Tabs
  const tabs = [
    { id: 'forms', label: 'Forms', icon: FileText },
    { id: 'responses', label: 'Responses', icon: LayoutList },
    { id: 'team', label: 'Team Members', icon: Users },
    { id: 'templates', label: 'Email Templates', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="onboarding-admin">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Client Onboarding</h1>
              <p className="text-sm text-gray-400 mt-1">Create and manage onboarding forms for new clients</p>
            </div>
            <Button 
              onClick={fetchData}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-black'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        ) : (
          <>
            {/* Forms Tab */}
            {activeTab === 'forms' && (
              <div className="space-y-6">
                {/* Actions */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">All Forms ({forms.length})</h2>
                  <div className="flex gap-3">
                    <Button
                      onClick={createDefaultForm}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Create Default Template
                    </Button>
                    <Button
                      onClick={createNewForm}
                      className="bg-white text-black hover:bg-gray-200"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Form
                    </Button>
                  </div>
                </div>

                {/* Forms Grid */}
                {forms.length === 0 ? (
                  <div className="text-center py-20 border border-dashed border-white/20 rounded-2xl">
                    <FileText className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No forms yet</h3>
                    <p className="text-gray-400 mb-4">Create your first onboarding form to get started</p>
                    <Button onClick={createDefaultForm} className="bg-white text-black hover:bg-gray-200">
                      Create Default Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {forms.map((form) => (
                      <div
                        key={form.id}
                        className="bg-[#111] border border-white/10 rounded-xl p-5 hover:border-white/30 transition-all group"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-white font-medium truncate">{form.name}</h3>
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{form.description || 'No description'}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${formStatusColors[form.status]}`}>
                            {form.status}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <span>{form.response_count || 0} responses</span>
                          <span>•</span>
                          <span>{formatDate(form.created_at)}</span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                          {form.status === 'published' ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 border-white/20 text-white hover:bg-white/10"
                                onClick={() => copyShareLink(form)}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy Link
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-white hover:bg-white/10"
                                onClick={() => generateQRCode(form)}
                              >
                                <QrCode className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-white hover:bg-white/10"
                                onClick={() => window.open(`/onboarding/${form.slug}`, '_blank')}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => toggleFormStatus(form.id, form.status)}
                            >
                              Publish
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/10"
                            onClick={() => duplicateForm(form.id)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => deleteForm(form.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Responses Tab */}
            {activeTab === 'responses' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                    <p className="text-2xl font-bold text-white">{stats.total_responses || 0}</p>
                    <p className="text-sm text-gray-400">Total Responses</p>
                  </div>
                  <div className="bg-[#111] border border-blue-500/30 rounded-xl p-4">
                    <p className="text-2xl font-bold text-blue-400">{stats.new_responses || 0}</p>
                    <p className="text-sm text-gray-400">New</p>
                  </div>
                  <div className="bg-[#111] border border-cyan-500/30 rounded-xl p-4">
                    <p className="text-2xl font-bold text-cyan-400">{stats.assigned_responses || 0}</p>
                    <p className="text-sm text-gray-400">Assigned</p>
                  </div>
                  <div className="bg-[#111] border border-orange-500/30 rounded-xl p-4">
                    <p className="text-2xl font-bold text-orange-400">{stats.in_progress || 0}</p>
                    <p className="text-sm text-gray-400">In Progress</p>
                  </div>
                  <div className="bg-[#111] border border-green-500/30 rounded-xl p-4">
                    <p className="text-2xl font-bold text-green-400">{stats.completed || 0}</p>
                    <p className="text-sm text-gray-400">Completed</p>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by company, contact, or form name..."
                      className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-[#111] border border-white/10 text-white rounded-lg px-4 py-2"
                  >
                    <option value="">All Statuses</option>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Responses List */}
                {filteredResponses.length === 0 ? (
                  <div className="text-center py-20 border border-dashed border-white/20 rounded-2xl">
                    <LayoutList className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No responses yet</h3>
                    <p className="text-gray-400">Responses will appear here when clients submit forms</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredResponses.map((response) => (
                      <div
                        key={response.id}
                        className="bg-[#111] border border-white/10 rounded-xl p-4 hover:border-white/30 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-white font-medium">{response.company_name || 'Unknown Company'}</h3>
                              <p className="text-sm text-gray-400">{response.contact_name || 'No contact'} • {response.form_name}</p>
                              <p className="text-xs text-gray-500 mt-1">{formatDate(response.submitted_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[response.status]}`}>
                              {statusLabels[response.status]}
                            </span>
                            {response.assigned_da_name && (
                              <span className="text-sm text-gray-400">
                                Assigned to: {response.assigned_da_name}
                              </span>
                            )}
                            <div className="flex items-center gap-2">
                              <select
                                value={response.status}
                                onChange={(e) => updateResponseStatus(response.id, e.target.value)}
                                className="bg-black border border-white/20 text-white rounded-lg px-2 py-1 text-sm"
                              >
                                {Object.entries(statusLabels).map(([value, label]) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                              <Button
                                size="sm"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                                onClick={() => {
                                  setSelectedResponseForDA(response);
                                  setShowSendToDAModal(true);
                                }}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Send to DA
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-white hover:bg-white/10"
                                onClick={() => setSelectedResponse(response)}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Team Members Tab */}
            {activeTab === 'team' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Team Members ({teamMembers.length})</h2>
                  <Button
                    onClick={() => setShowAddTeamMember(true)}
                    className="bg-white text-black hover:bg-gray-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Team Member
                  </Button>
                </div>

                {teamMembers.length === 0 ? (
                  <div className="text-center py-20 border border-dashed border-white/20 rounded-2xl">
                    <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No team members</h3>
                    <p className="text-gray-400 mb-4">Add Digital Assistants to assign onboarding submissions</p>
                    <Button onClick={() => setShowAddTeamMember(true)} className="bg-white text-black hover:bg-gray-200">
                      Add Team Member
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        className="bg-[#111] border border-white/10 rounded-xl p-4 flex items-center gap-4"
                      >
                        <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-medium">{member.name}</h3>
                          <p className="text-sm text-gray-400">{member.email}</p>
                          <p className="text-xs text-cyan-400">{member.role}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => deleteTeamMember(member.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Email Templates Tab */}
            {activeTab === 'templates' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Email Templates</h2>
                  <Button className="bg-white text-black hover:bg-gray-200">
                    <Plus className="w-4 h-4 mr-2" />
                    New Template
                  </Button>
                </div>

                <div className="text-center py-20 border border-dashed border-white/20 rounded-2xl">
                  <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Email templates coming soon</h3>
                  <p className="text-gray-400">Create custom email templates for DA assignments and notifications</p>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-white">Settings</h2>
                
                <div className="bg-[#111] border border-white/10 rounded-xl p-6 space-y-6">
                  <div>
                    <h3 className="text-white font-medium mb-2">Notification Settings</h3>
                    <p className="text-sm text-gray-400 mb-4">Configure how you receive notifications for new submissions</p>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded" />
                        <span className="text-white">Email notifications for new submissions</span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded" />
                        <span className="text-white">In-app notifications</span>
                      </label>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/10">
                    <h3 className="text-white font-medium mb-2">Default Form Settings</h3>
                    <p className="text-sm text-gray-400 mb-4">Set default values for new forms</p>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-gray-400 block mb-1">Default Logo URL</label>
                        <Input
                          placeholder="https://example.com/logo.png"
                          className="bg-black border-white/20 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400 block mb-1">Default Cover Image URL</label>
                        <Input
                          placeholder="https://example.com/cover.jpg"
                          className="bg-black border-white/20 text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">QR Code</h2>
              <button onClick={() => setShowQRModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-4">{qrFormName}</p>
            <div className="bg-white p-4 rounded-xl flex items-center justify-center">
              <img src={qrCodeData} alt="QR Code" className="w-full max-w-[250px]" />
            </div>
            <Button
              className="w-full mt-4 bg-white text-black hover:bg-gray-200"
              onClick={() => {
                const link = document.createElement('a');
                link.download = `${qrFormName.replace(/\s+/g, '-')}-qr.png`;
                link.href = qrCodeData;
                link.click();
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download QR Code
            </Button>
          </div>
        </div>
      )}

      {/* Send to DA Modal */}
      {showSendToDAModal && selectedResponseForDA && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Send to Digital Assistant</h2>
              <button onClick={() => setShowSendToDAModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Select a Digital Assistant to assign this onboarding submission
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {teamMembers.length === 0 ? (
                <p className="text-center text-gray-400 py-4">No team members available. Add team members first.</p>
              ) : (
                teamMembers.map((member) => (
                  <button
                    key={member.id}
                    onClick={() => sendToDA(selectedResponseForDA.id, member.id)}
                    className="w-full flex items-center gap-3 p-3 bg-black/50 rounded-xl hover:bg-white/5 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{member.name}</p>
                      <p className="text-sm text-gray-400">{member.email}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Team Member Modal */}
      {showAddTeamMember && (
        <AddTeamMemberModal
          onClose={() => setShowAddTeamMember(false)}
          onAdd={addTeamMember}
        />
      )}

      {/* Response Detail Modal */}
      {selectedResponse && (
        <ResponseDetailModal
          response={selectedResponse}
          onClose={() => setSelectedResponse(null)}
        />
      )}
    </div>
  );
}

// Add Team Member Modal Component
function AddTeamMemberModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Digital Assistant');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error('Name and email are required');
      return;
    }
    onAdd(name, email, role);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Add Team Member</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Name *</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="bg-black border-white/20 text-white"
              required
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Email *</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              className="bg-black border-white/20 text-white"
              required
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Role</label>
            <Input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Digital Assistant"
              className="bg-black border-white/20 text-white"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-white/20 text-white">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-white text-black hover:bg-gray-200">
              Add Member
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Response Detail Modal Component
function ResponseDetailModal({ response, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-3xl w-full my-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">{response.company_name || 'Submission Details'}</h2>
            <p className="text-sm text-gray-400">{response.form_name} • Submitted {formatDate(response.submitted_at)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
          {/* Contact Info */}
          <div className="bg-black/50 rounded-xl p-4">
            <h3 className="text-white font-medium mb-3">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Contact Name</p>
                <p className="text-white">{response.contact_name || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400">Email</p>
                <p className="text-white">{response.contact_email || '-'}</p>
              </div>
            </div>
          </div>

          {/* All Answers */}
          <div className="bg-black/50 rounded-xl p-4">
            <h3 className="text-white font-medium mb-3">Form Responses</h3>
            <div className="space-y-4">
              {response.answers && Object.entries(response.answers).map(([key, value]) => (
                <div key={key} className="border-b border-white/10 pb-3 last:border-0">
                  <p className="text-gray-400 text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="text-white mt-1">
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : value || '-'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Uploaded Files */}
          {response.files && Object.keys(response.files).length > 0 && (
            <div className="bg-black/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">Uploaded Files ({Object.keys(response.files).length})</h3>
              <div className="space-y-2">
                {Object.entries(response.files).map(([fieldId, fileUrl]) => (
                  <div key={fieldId} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <span className="text-white text-sm capitalize">{fieldId.replace(/_/g, ' ')}</span>
                    <Button size="sm" variant="outline" className="border-white/20 text-white">
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status & Assignment */}
          <div className="bg-black/50 rounded-xl p-4">
            <h3 className="text-white font-medium mb-3">Status</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Current Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border mt-1 ${statusColors[response.status]}`}>
                  {statusLabels[response.status]}
                </span>
              </div>
              {response.assigned_da_name && (
                <div>
                  <p className="text-gray-400">Assigned To</p>
                  <p className="text-white">{response.assigned_da_name}</p>
                  <p className="text-xs text-gray-500">{formatDate(response.assigned_at)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/20 text-white">
            Close
          </Button>
          <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Download className="w-4 h-4 mr-2" />
            Download All Files
          </Button>
        </div>
      </div>
    </div>
  );
}
